import React, { useState } from 'react';

import { useToast } from '@chakra-ui/react';

import { deleteCustomers, fetchPaginatedCustomers } from 'api/customers';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { CustomersProps } from './Customers.props';

import CustomersView from './Customers.view';

const CustomersContainer = (_: CustomersProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const queryKey = [`fetch-customers-${washHubId}`, page];

  const queryFn = () =>
    fetchPaginatedCustomers({
      page,
      limit: 10,
      washHub: washHubId,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const { mutate, isLoading: isDeleting } = useMutation(deleteCustomers);

  useWashHubListener(() => {
    setPage(1);
  });

  const customers = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onDelete = (ids: string[], _onSuccess: () => void) =>
    mutate(ids, {
      onSuccess: () => {
        toast({
          duration: 3000,
          isClosable: true,
          status: 'success',
          title: 'Success',
          description: 'Customers Deleted',
        });

        queryClient.invalidateQueries(`fetch-customers-${washHubId}`);
        queryClient.invalidateQueries(`fetch-customers-${washHubId}-select`, {
          exact: true,
        });

        _onSuccess();
      },
    });

  const _props = {
    customers,
    isLoading: isLoading || isFetching,
    isDeleting,
    pagination,
    onDelete,
  };

  return <CustomersView {..._props} />;
};

export default CustomersContainer;
